import { useState } from 'react';
import { api } from '../api';
import type { WorkspaceResponse } from '../api';
import { useResource } from '../hooks';
import { ErrorNotice, SkeletonPage } from '../components/ui';
import { Callout, Section } from '../components/primitives';
import { subscriptionStatusLabel as statusLabel } from '../labels';
import { LicencePanel } from '../components/licence/LicencePanel';

/**
 * Settings ▸ Billing (CO-160): the plan first — what it allows, how much is
 * used, who pays for it — then the subscription and its action. The plan's
 * state is the page's one conclusion about whether the product may be used
 * (CO-184, OR13); the subscription below it is evidence and carries only its
 * own state, with no second "entitled" word. When the two disagree (no
 * subscription, yet a plan Hestaworks assigned), a Plan row says whose plan
 * it is, so "No subscription" never reads as "no plan".
 */
export function BillingPage({ workspace }: { workspace: WorkspaceResponse }) {
  const billing = useResource(() => api.billingStatus(), [], undefined, { key: 'billing' });
  const licence = useResource(() => api.licence(), [], undefined, { key: 'licence' });
  const [startingCheckout, setStartingCheckout] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const isOwner = workspace.identity.role?.is_owner_role ?? false;

  async function handleCheckout() {
    setStartingCheckout(true);
    setError(null);
    try {
      const { checkout_url } = await api.createCheckoutSession();
      window.location.href = checkout_url;
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
      setStartingCheckout(false);
    }
  }

  const status = billing.data?.subscription_status;
  // A plan Hestaworks assigned runs without a subscription.
  const assignedPlan =
    licence.data?.plan && licence.data.licence?.source !== 'stripe' && (licence.data.status === 'active' || licence.data.status === 'grace')
      ? licence.data.plan.name
      : null;

  return (
    <div className="billing">
      <LicencePanel isOwner={isOwner} />

      <Section title="Subscription" testId="billing-subscription">
        {billing.loading && !billing.data ? <SkeletonPage cards={0} rows={1} /> : null}
        <ErrorNotice error={billing.error ?? error} onRetry={billing.error ? billing.reload : undefined} />
        {billing.data ? (
          <>
            <dl className="settings-facts" data-testid="billing-status">
              <div>
                <dt>Status</dt>
                <dd>
                  {statusLabel[billing.data.subscription_status] ?? billing.data.subscription_status}
                </dd>
              </div>
              {billing.data.subscription_current_period_end ? (
                <div>
                  <dt>Current period ends</dt>
                  <dd>{new Date(billing.data.subscription_current_period_end).toLocaleDateString()}</dd>
                </div>
              ) : null}
              {assignedPlan && status === 'none' ? (
                <div data-testid="billing-plan-source">
                  <dt>Plan</dt>
                  <dd>{assignedPlan} · assigned by Hestaworks</dd>
                </div>
              ) : null}
            </dl>
            {!billing.data.real ? (
              <Callout tone="info">Checkout here is a sandbox: no Stripe account is configured, so no payment is taken.</Callout>
            ) : null}
            {isOwner && !billing.data.entitled ? (
              <div className="billing__actions">
                <button
                  className={assignedPlan ? 'button button--secondary' : 'button button--primary'}
                  type="button"
                  onClick={handleCheckout}
                  disabled={startingCheckout}
                >
                  {startingCheckout ? 'Starting checkout' : 'Subscribe'}
                </button>
              </div>
            ) : null}
            {!isOwner ? <p className="muted">Only an owner can manage billing.</p> : null}
          </>
        ) : null}
      </Section>
    </div>
  );
}
